import { useState, useEffect } from "react";
import { User, Mail, Camera } from "lucide-react";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { userApi } from "../../services/userApi";
import { useAuthStore } from "../../stores/authStore";
import toast from "react-hot-toast";

export default function ProfileSetupModal({ isOpen, onClose }) {
  const { user, setUser } = useAuthStore();
  const [form, setForm] = useState({ name: "", email: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      setForm({
        name: user.name?.startsWith("User") ? "" : user.name || "",
        email: user.email || "",
      });
    }
  }, [isOpen, user]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    setSaving(true);
    try {
      const res = await userApi.updateProfile(form);
      const updated = res.data?.data || res.data;
      if (updated) {
        setUser({ ...user, ...updated });
      } else {
        setUser({ ...user, name: form.name, email: form.email });
      }
      toast.success("Profile updated!");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} title="" maxWidth="sm">
      <div className="px-2 pb-2">
        <div className="text-center mb-6">
          <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
            <User size={36} className="text-primary-600" />
          </div>
          <h2 className="text-xl font-bold text-neutral-900">
            Complete Your Profile
          </h2>
          <p className="text-sm text-neutral-500 mt-1">
            Let us know a bit about you for a better experience
          </p>
        </div>

        <div className="space-y-4">
          <Input
            label="Full Name"
            placeholder="Enter your full name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
          />
          <Input
            label="Email (optional)"
            type="email"
            placeholder="your@email.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
          />

          <Button
            className="w-full"
            onClick={handleSave}
            loading={saving}
            disabled={!form.name.trim()}
          >
            Save & Continue
          </Button>

          <button
            onClick={onClose}
            className="w-full text-sm text-neutral-400 hover:text-neutral-600 transition-colors py-1"
          >
            I'll do this later
          </button>
        </div>
      </div>
    </Modal>
  );
}
